import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useJwtToken } from '@me8eon/react_authentication'
import { useTranslation, type TranslationFn } from '@me8eon/translation'
import { listIncidents } from '../api/incidents'
import { NEXTHINK_TASK_STATUS_LABELS } from '../constants/nexthinkTaskStatus'
import { useNexthinkActionTracker } from '../contexts/NexthinkActionTrackerProvider'
import type { NexthinkTrackedAction } from '../types/nexthinkTracker'
import {
  buildTicketSidebarItems,
  humanizeToolName,
  incidentRecordToTicketItem,
  type SidebarItem,
  type SidebarTaskItem,
  type SidebarTicketItem,
} from '../utils/ticketSidebarItems'
import { useChat } from './useChat'
import { usePageVisible } from './usePageVisible'

const TICKET_REFRESH_INTERVAL_MS = 30 * 1000

function trackedActionToTaskItem(t: TranslationFn, action: NexthinkTrackedAction): SidebarTaskItem {
  const labels = action.actionId ? NEXTHINK_TASK_STATUS_LABELS[action.actionId] : undefined
  const statusKey = action.status === 'pending' ? 'progress' : action.status
  const translationKey = labels?.[statusKey]

  return {
    kind: 'task',
    id: action.requestId,
    label: translationKey
      ? t(translationKey)
      : action.actionId
        ? humanizeToolName(action.actionId)
        : t('chatBot.rightSidebar.defaultTaskLabel'),
    status: action.status,
  }
}

function ticketIds(items: readonly SidebarItem[]): Set<string> {
  const ids = new Set<string>()
  for (const item of items) if (item.kind === 'ticket') ids.add(item.id)
  return ids
}

/** A `null` baseline is unseeded: the first observation never counts as new. */
function hasNewId(current: Set<string>, baseline: Set<string> | null): boolean {
  if (baseline === null) return false
  for (const id of current) if (!baseline.has(id)) return true
  return false
}

export interface ActivitySidebarData {
  items: SidebarItem[]
  /**
   * Increments only when the backend incidents list reports a ticket id not
   * seen before; `RightSidebar` auto-opens on an increase. A live tool result
   * is never proof of creation, it only triggers an early backend check.
   */
  ticketCreationTick: number
}

/**
 * Merges live chat tasks/tickets, backend incidents and tracked Nexthink
 * actions for the Activities panel. Incidents are polled only while `polling`
 * is `true` and the tab is visible; otherwise they refresh once on mount and
 * on a live ticket hint.
 */
export function useActivitySidebarItems({ polling }: { polling: boolean }): ActivitySidebarData {
  const { state } = useChat()
  const jwtTokenFn = useJwtToken()
  const t = useTranslation()
  const pageVisible = usePageVisible()
  const { actions: trackedActions } = useNexthinkActionTracker()
  const liveItems = useMemo(
    () => buildTicketSidebarItems(state.messages),
    [state.messages],
  )
  const [fetchedTickets, setFetchedTickets] = useState<SidebarItem[]>([])
  const [ticketCreationTick, setTicketCreationTick] = useState(0)

  // `null` = not yet seeded: the first response only seeds the baseline.
  // Only a later new id may bump `ticketCreationTick`.
  const fetchBaselineRef = useRef<Set<string> | null>(null)
  const inFlightRef = useRef(false)
  const mountedRef = useRef(true)

  const refresh = useCallback(async () => {
    if (inFlightRef.current) return
    inFlightRef.current = true
    try {
      const response = await listIncidents(jwtTokenFn, true)
      if (!mountedRef.current) return
      const tickets = (response.result?.queryResult ?? [])
        .map(incidentRecordToTicketItem)
        .filter((ticket): ticket is SidebarTicketItem => ticket !== undefined)
      const currentFetchedIds = ticketIds(tickets)
      if (hasNewId(currentFetchedIds, fetchBaselineRef.current)) {
        setTicketCreationTick((tick) => tick + 1)
      }
      fetchBaselineRef.current = currentFetchedIds
      setFetchedTickets(tickets)
    } catch (error) {
      if (mountedRef.current) {
        console.warn('Activities panel: failed to refresh ticket status', error)
      }
    } finally {
      inFlightRef.current = false
    }
  }, [jwtTokenFn])

  useEffect(() => {
    mountedRef.current = true
    void refresh()
    return () => {
      mountedRef.current = false
    }
  }, [refresh])

  const shouldPoll = polling && pageVisible
  useEffect(() => {
    if (!shouldPoll) return
    void refresh()
    const interval = window.setInterval(() => void refresh(), TICKET_REFRESH_INTERVAL_MS)
    return () => window.clearInterval(interval)
  }, [shouldPoll, refresh])

  // Re-seeded per thread so tickets already in a reloaded history don't count
  // as new. A new live ticket only prompts an early backend check.
  const liveBaselineRef = useRef<Set<string> | null>(null)

  useEffect(() => {
    if (state.isLoadingThread) {
      liveBaselineRef.current = null
      return
    }
    const currentLiveTicketIds = ticketIds(liveItems)
    if (hasNewId(currentLiveTicketIds, liveBaselineRef.current)) {
      void refresh()
    }
    liveBaselineRef.current = currentLiveTicketIds
  }, [state.isLoadingThread, liveItems, refresh])

  // `orderRef` records when each id was first seen so tickets and tasks sort
  // newest-first together instead of grouped by source.
  const orderCounterRef = useRef(0)
  const orderRef = useRef(new Map<string, number>())

  const items = useMemo(() => {
    const byId = new Map<string, SidebarItem>()
    for (const item of liveItems) byId.set(item.id, item)
    for (const ticket of fetchedTickets) byId.set(ticket.id, ticket)
    for (const action of trackedActions) byId.set(action.requestId, trackedActionToTaskItem(t, action))

    for (const id of byId.keys()) {
      if (!orderRef.current.has(id)) {
        orderRef.current.set(id, orderCounterRef.current++)
      }
    }

    return Array.from(byId.values()).sort(
      (a, b) => (orderRef.current.get(b.id) ?? 0) - (orderRef.current.get(a.id) ?? 0),
    )
  }, [liveItems, fetchedTickets, trackedActions, t])

  return { items, ticketCreationTick }
}
